import React, { useEffect, useRef, useState, useCallback } from "react";
import useDebouncedCallback from "@builder/hooks/useDebounceCallback";
import Button from "@builder/components/general/Button";
import OliverText from "@builder/components/general/OliverText";
import { useAppSelector } from "@builder/store/store";
import { ColorPicker } from "antd";
import { useTranslation } from "react-i18next";
import TextInput from "../TextInput";
import "./style.scss";
import GlobalColorModal from "./GlobalColorModal/GlobalColorModal";
import TooltipElement from "../Tooltip";

interface ColorInputProps {
    title?: string;
    color?: string;
    callback: (value: string) => void;
    options?: { disableGlobalColors?: boolean; noText?: boolean };
}

export interface GlobalColor {
    id: string;
    name: string;
    color: string;
}

const ColorInput: React.FC<ColorInputProps> = ({ title, color, callback, options }: ColorInputProps) => {
    const globalColors = useAppSelector(state => state.builder.build.extraAttributes?.buildGlobalColors);
    const { t } = useTranslation();
    const [hex, setHex] = useState<string | undefined>(typeof color === "string" ? color : undefined);
    const [selectedGlobalColor, setSelectedGlobalColor] = useState<GlobalColor | undefined>(undefined);
    const colorInputRef = useRef<HTMLDivElement>(null);
    const [globalColorsModal, setGlobalColorsModal] = useState<"add" | "choose" | undefined>();
    const selectedNode = useAppSelector(state => state.builder.selectedNode);
    const buildSettingsKey = useAppSelector(state => state.buildSettingsKey);

    useEffect(() => {
        if (color && typeof color === "string") {
            const isGlobalColor = color.match(/var\(--(.*?)\)/);
            if (isGlobalColor) {
                const globalColor = globalColors?.find(globalColor => globalColor.id === isGlobalColor[1]);
                setHex(
                    buildSettingsKey?.[globalColor?.color || ""]
                        ? buildSettingsKey?.[globalColor?.color || ""].placeholder
                        : globalColor?.color || hex
                );
                setSelectedGlobalColor(globalColor);
            } else {
                setSelectedGlobalColor(undefined);
                setHex(color);
            }
        } else {
            setSelectedGlobalColor(undefined);
            setHex(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [color, globalColors, buildSettingsKey]);

    const debouncedCallback = useDebouncedCallback(callback, 500);

    const handleColorChange = useCallback(
        (_: any, hexValue: string) => {
            if (hexValue && !hexValue.startsWith("#")) hexValue = "#" + hexValue;
            // a fix of an absene bug of auto adding 00 after the hex value for no reason
            // I had no choice but to detec when it's happening and manualy correct this as well as force rerendering of the color picker
            if (hexValue.length === 9 && hexValue.endsWith("00") && hex && hex?.length === 7 && !hex.endsWith("00")) {
                hexValue = hex.slice(0, 6) + "1"; //forces rerendering of the color picker without closing the modal. I know it's insane but it won't actually affect the UX
            }
            setSelectedGlobalColor(undefined);
            setHex(hexValue);
            debouncedCallback(hexValue);
        },
        [debouncedCallback]
    );

    return (
        <>
            <div className="color-input-wrapper">
                {title && <OliverText text={t("generalComponents.colorInput." + title)} size="xs" />}
                <div className="color-input-row">
                    <div className="left">
                        <ColorPicker value={hex || ""} onChange={handleColorChange} key={selectedNode + (selectedGlobalColor?.id || "")} />
                        {options?.noText ||
                            (selectedGlobalColor?.name ? (
                                <OliverText text={selectedGlobalColor.name} size="xs" color="base-blue" />
                            ) : (
                                <TextInput
                                    value={hex}
                                    placeholder="#ffffff"
                                    callback={c => {
                                        handleColorChange(null, c);
                                    }}
                                    noTranslate
                                />
                            ))}
                    </div>
                    {options?.disableGlobalColors || options?.noText || (
                        <div className="right" ref={colorInputRef}>
                            <Button
                                buttonStyle="grey"
                                size="fit"
                                className="add-elem-button"
                                onClick={() => {
                                    setGlobalColorsModal("choose");
                                }}
                                icon={{ src: "/globe.svg" }}
                            />
                            {!!selectedGlobalColor ||
                                (hex && (
                                    <TooltipElement text={t("tooltip.dynamicColor")} extraStyle={{ width: "200px" }}>
                                        <Button
                                            buttonStyle="secondary"
                                            size="fit"
                                            className="add-elem-button"
                                            onClick={() => {
                                                setGlobalColorsModal("add");
                                            }}
                                            icon={{ src: "/plus-icon-white.svg" }}
                                        />
                                    </TooltipElement>
                                ))}
                            {globalColorsModal && (
                                <GlobalColorModal
                                    anchorEl={colorInputRef.current}
                                    type={globalColorsModal}
                                    setClose={() => setGlobalColorsModal(undefined)}
                                    currentHex={hex}
                                    setTheColor={callback}
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default ColorInput;
